import { useState, useEffect } from 'react';
import { Modal, Spinner, Stack } from 'ui-components';
import { useCreateCouponMutation, useGetCheckedInApiUsersLazyQuery } from 'drycut-graphql';
import { CouponFormData, SelectOption } from './types';
import { CouponDetailsStep } from './CouponDetailsStep';
import { StylistAuthStep } from './StylistAuthStep';
import { CouponSuccessStep } from './CouponSuccessStep';
import { NoStylistsAvailableWarning } from './NoStylistsAvailableWarning';

type Step = 'details' | 'auth' | 'success';

type Customer = {
  id: string;
  name: string;
  phoneNumber: string;
};

type Props = {
  open: boolean;
  onClose: () => void;
};

export function CreateCouponModal({ open, onClose }: Props): React.ReactElement {
  const [step, setStep] = useState<Step>('details');
  const [selectedType, setSelectedType] = useState<SelectOption | undefined>();
  const [customer, setCustomer] = useState<Customer | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [pendingFormData, setPendingFormData] = useState<CouponFormData | null>(null);
  const [couponCode, setCouponCode] = useState<string | null>(null);

  const [getCheckedInApiUsers, { data: apiUsersData, loading: apiUsersLoading }] =
    useGetCheckedInApiUsersLazyQuery();
  const [createCoupon, { loading }] = useCreateCouponMutation();

  // Reset state when modal opens and fetch checked-in API users
  useEffect(() => {
    if (open) {
      setStep('details');
      setSelectedType(undefined);
      setCustomer(null);
      setError(null);
      setPendingFormData(null);
      setCouponCode(null);
      getCheckedInApiUsers({ fetchPolicy: 'network-only' });
    }
  }, [open, getCheckedInApiUsers]);

  const handleTypeChange = (option: SelectOption) => {
    setSelectedType(option);
    setError(null);
  };

  const handleDetailsSubmit = (data: CouponFormData) => {
    setPendingFormData(data);
    setStep('auth');
  };

  const isGiftCard = selectedType?.value === 'Coupons::Goodwills::GiftCard';

  const handleAuthenticated = async (stylistId: string) => {
    if (!pendingFormData || (!customer && !isGiftCard)) return;

    try {
      setError(null);

      const result = await createCoupon({
        variables: {
          input: {
            type: selectedType?.value || '',
            reason: pendingFormData.reason,
            customerId: customer?.id,
            stylistId,
          },
        },
      });

      if (result.data?.createCoupon?.coupon) {
        setCouponCode(result.data.createCoupon.coupon.code);
        setStep('success');
      } else if (result.data?.createCoupon?.errors && result.data.createCoupon.errors.length > 0) {
        const errorMsg = result.data.createCoupon.errors.map((e) => e.message).join(', ');
        setError(errorMsg);
        setStep('details');
      }
    } catch (err: unknown) {
      const errorMessage = err instanceof Error ? err.message : 'Nicht-Ausstellung des Gutscheins';
      setError(errorMessage);
      setStep('details');
    }
  };

  const handleBack = () => {
    setStep('details');
  };

  const handleClose = () => {
    onClose();
  };

  // Control variables
  const apiUsers = apiUsersData?.checkedInApiUsers || [];
  const loadedWithUsers = !apiUsersLoading && apiUsers.length > 0;

  return (
    <Modal
      open={open}
      onClose={handleClose}
      title={step === 'success' ? 'Gutschein erstesllt' : 'Gutschein erstellen'}
      closeButton
      overlayClose={!loading}
    >
      {apiUsersLoading && (
        <Stack className="w-80 py-16 items-center">
          <Spinner className="size-16 text-primary-600 opacity-80" />
        </Stack>
      )}

      {!apiUsersLoading && apiUsers.length === 0 && (
        <NoStylistsAvailableWarning handleClose={handleClose} loading={loading} />
      )}

      {loadedWithUsers && step === 'details' && (
        <CouponDetailsStep
          onSubmit={handleDetailsSubmit}
          onCancel={handleClose}
          loading={loading}
          selectedType={selectedType}
          onTypeChange={handleTypeChange}
          customer={customer}
          onCustomerChange={setCustomer}
          reason={pendingFormData?.reason}
          error={error}
        />
      )}

      {loadedWithUsers && step === 'auth' && (
        <StylistAuthStep
          creatingCoupon={loading}
          onAuthenticated={handleAuthenticated}
          onBack={handleBack}
          onCancel={handleClose}
          apiUsers={apiUsers}
          couponSummary={{
            customer: { name: customer?.name || '', phoneNumber: customer?.phoneNumber || '' },
            couponType: selectedType?.label || '',
            reason: pendingFormData?.reason || '',
          }}
        />
      )}

      {loadedWithUsers && step === 'success' && couponCode && (
        <CouponSuccessStep
          couponCode={couponCode}
          customer={customer?.name}
          couponType={selectedType?.label || ''}
          onClose={handleClose}
        />
      )}
    </Modal>
  );
}
